import { useTranslation } from "react-i18next";
import { useApiLimitStatus } from "../../../../hooks/useApiLimitStatus.tsx";

interface FeatureContent {
	title?: string | string[];
	showTitleIcon?: boolean;
	features?: {
		title?: string;
		items?: string[];
	}[];
	minHeight?: number;
	tooltip?: string;
}

interface FeatureContentMap {
	[key: string]: {
		[key: string]: FeatureContent;
	};
}

export const usePricingFeatureContent = () => {
	const { t } = useTranslation("translation", { keyPrefix: "pricingFlow.pricingPopup" });
	const { isUserCreatedBeforeGrowthDate } = useApiLimitStatus();
	const freeTrialImageApiLabel = isUserCreatedBeforeGrowthDate ? t("freeFor14Days14days") : t("freeFor14Days");

	const expandedFeatures: string[] = ["imageGenerationApi", "imageEditingApi", "videoEditingApi", "productShotApi"];

	const featureMinHeights: { [key: string]: number } = {
		flexibleDeployment: 86,
		integrateAnyWayYouWant: 110,
		visualBirthCertificatePerProjectUnit: 68,
		sourceCodeAndModelWeights: 68,
		fineTuningSuite: 86,
		throughput: 66,
		latency: 66,
		indemnification: 66,
		whiteGloveOptions: 66,
		premiumPlatformSecurity: 66,
	};

	const featureContent: FeatureContentMap = {
		freeTrial: {
			imageGenerationApi: {
				title: freeTrialImageApiLabel,
			},
			imageEditingApi: {
				title: "",
			},
			videoEditingApi: {
				title: "",
			},
			productShotApi: {
				title: "",
			},
			flexibleDeployment: {
				title: "-",
			},
			integrateAnyWayYouWant: {
				title: "all",
			},
			liveVideoStreaming: {
				title: "-",
			},
			visualBirthCertificatePerProjectUnit: {
				title: "-",
				tooltip: t("visualBirthCertificatePerProjectUnitTooltip"),
			},
			sourceCodeAndModelWeights: {
				title: "-",
			},
			copyrightabilityReport: {
				title: "-",
			},
			fineTuningSuite: {
				title: t("selfServiceFineTuningViaPlayground"),
			},
			throughput: {
				title: t("10actionsPerMinute"),
			},
			latency: {
				title: t("queueBasedPriority"),
			},
			indemnification: {
				title: "-",
			},
			whiteGloveOptions: {
				title: "-",
			},
			premiumPlatformSecurity: {
				title: "-",
			},
		},
		selfService: {
			imageGenerationApi: {
				title: t("pricingFeatures.imageGenerationApi.title"),
				features: [
					{
						items: [t("fibo$0.032/image"), t("fiboLite$0.0288/image"), t("fiboStructuredPrompt$0.02/call")],
					},
				],
			},
			imageEditingApi: {
				title: t("pricingFeatures.imageEditingApi.title"),
				features: [
					{
						items: [
							t("backgroundRemoval$0.018/image"),
							t("guidedBackgroundRemoval$0.025/image"),
							t("backgroundGeneration$0.032/image"),
							t("eraser$0.032/image"),
							t("genFill$0.032/image"),
							t("imageExpansion$0.032/image"),
							t("increaseResolution$0.032/image"),
						],
					},
				],
			},
			videoEditingApi: {
				title: t("pricingFeatures.videoEditingApi.title"),
				features: [
					{
						items: [
							t("videoRemoveBackground$0.04/sec"),
							t("videoIncreaseResolution$0.06/sec"),
							t("videoEraser$0.112/sec"),
						],
					},
				],
			},
			productShotApi: {
				title: t("pricingFeatures.productShotApi.title"),
				features: [
					{
						items: [
							t("lifestyleByText$0.032/image"),
							t("lifestyleByImage$0.032/image"),
							t("productCutOut$0.018/image"),
							t("productShadow$0.018/image"),
						],
					},
				],
			},
			flexibleDeployment: {
				title: "-",
			},
			integrateAnyWayYouWant: {
				title: "all",
			},
			liveVideoStreaming: {
				title: "-",
			},
			visualBirthCertificatePerProjectUnit: {
				title: "-",
			},
			sourceCodeAndModelWeights: {
				title: "-",
			},
			copyrightabilityReport: {
				title: "-",
			},
			fineTuningSuite: {
				title: "-",
			},
			throughput: {
				title: t("60actionsPerMinute"),
			},
			latency: {
				title: t("prioritizedQueue"),
			},
			indemnification: {
				title: t("standardIndemnification"),
				tooltip: t("standardIndemnificationTooltip"),
			},
			whiteGloveOptions: {
				title: "-",
			},
			premiumPlatformSecurity: {
				title: "-",
			},
		},
		briaService: {
			imageGenerationApi: {
				title: t("customPrice"),
			},
			imageEditingApi: {
				title: "",
			},
			videoEditingApi: {
				title: "",
			},
			productShotApi: {
				title: "",
			},
			flexibleDeployment: {
				title: "-",
			},
			integrateAnyWayYouWant: {
				title: "all",
			},
			liveVideoStreaming: {
				title: "-",
			},
			visualBirthCertificatePerProjectUnit: {
				title: "-",
			},
			sourceCodeAndModelWeights: {
				title: "-",
			},
			copyrightabilityReport: {
				title: "-",
			},
			fineTuningSuite: {
				title: "-",
			},
			throughput: {
				title: t("150actionsPerMinute"),
			},
			latency: {
				title: t("sameAsDevelopment"),
			},
			indemnification: {
				title: t("fullIP&PrivacyIndemnity"),
			},
			whiteGloveOptions: {
				title: t("dedicatedCustomerSuccess"),
			},
			premiumPlatformSecurity: {
				title: "all",
			},
		},
		enterprise: {
			imageGenerationApi: {
				title: t("customPrice"),
			},
			imageEditingApi: {
				title: "",
			},
			videoEditingApi: {
				title: "",
			},
			productShotApi: {
				title: "",
			},
			flexibleDeployment: {
				title: [t("APIHostedOnYourPrivateCloud"), t("OnPrem")],
				showTitleIcon: false,
				features: [
					{
						title: t("APIHostedOnYourPrivateCloud"),
					},
					{
						title: t("OnPrem"),
					},
				],
			},
			integrateAnyWayYouWant: {
				title: "all",
			},
			liveVideoStreaming: {
				title: "all",
			},
			visualBirthCertificatePerProjectUnit: {
				title: "all",
			},
			sourceCodeAndModelWeights: {
				title: "-",
			},
			copyrightabilityReport: {
				title: "all",
			},
			fineTuningSuite: {
				title: t("sameAsBusiness"),
			},
			throughput: {
				title: t("customizedForYourNeeds"),
			},
			latency: {
				title: t("customizedForYourNeeds"),
			},
			indemnification: {
				title: t("sameAsBusiness"),
			},
			whiteGloveOptions: {
				title: t("dedicatedCustomerSuccessManager"),
			},
			premiumPlatformSecurity: {
				title: "all",
			},
		},
	};

	const getFeatureContent = (planKey: string, featureId: string): FeatureContent => {
		return featureContent[planKey]?.[featureId] || { title: "—", items: [] };
	};

	const availableAPIs: string[] = [
		t("pricingFeatures.imageGenerationApi.id"),
		t("pricingFeatures.imageEditingApi.id"),
		t("pricingFeatures.videoEditingApi.id"),
		t("pricingFeatures.productShotApi.id"),
	];

	return {
		featureContent,
		getFeatureContent,
		featureMinHeights,
		expandedFeatures,
		availableAPIs,
	};
};

export type { FeatureContent, FeatureContentMap };
